import React, { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useWindowWidth from '../../../common/UseWindowWidth';
import {
    clearPickedBetMatchLine,
    initializeBetMatchLine,
    quickPickBetMatchLines,
    setBetMatchLines,
} from '../../redux/soccerStoreSlice';
import { RootState } from '../../redux/store';
import { setDefaultStakes, setLines, setStakes } from '../../redux/storeSlice';
import { mapBetMatchLinesIntoLines, mapSessionLinesIntoBets } from '../utils/mappers';
import { calculateStakesByLineBets, validateLineBets } from '../utils/validate';

import { MatchesList } from './components/MatchesList';
import { Numbers } from './components/Numbers';

function Picker() {
    const soccerPlayStore = useSelector((state: RootState) => state.soccerPlayStore);
    const playStore = useSelector((state: RootState) => state.playStore);
    const dispatch = useDispatch();
    const scrollWrapper = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const windowWidth = useWindowWidth();
    const ticketsPerRowBreakpoints = [
        { width: 1250, tickets: 3 },
        { width: 1050, tickets: 2 },
        { width: 0, tickets: 1 },
    ];
    const ticketsPerRow = ticketsPerRowBreakpoints.find((breakpoint) => windowWidth >= breakpoint.width);
    const lastBetMatchLineRef = useRef<HTMLDivElement>(null);

    const clearPickedNumbers = (betMatchLineIndex: number) => {
        dispatch(clearPickedBetMatchLine({ betLinesIndex: betMatchLineIndex }));
    };

    const quickPickNumbers = (betMatchLineIndex: number) => {
        dispatch(quickPickBetMatchLines({ betLinesIndex: betMatchLineIndex }));
    };

    const validateLines = () => {
        if (soccerPlayStore.betMatchLines.length === 0) {
            return;
        }

        const validatedBetMatchLines = soccerPlayStore.betMatchLines.filter((line) => validateLineBets(line));
        const stakes = calculateStakesByLineBets(validatedBetMatchLines);
        dispatch(setStakes(stakes));

        if (validatedBetMatchLines.length) {
            dispatch(setLines(mapBetMatchLinesIntoLines(validatedBetMatchLines)));
        }

        if (
            validatedBetMatchLines.length >= soccerPlayStore.betMatchLines.length &&
            soccerPlayStore.betMatchLines.length < playStore.lottery.maxLinesPerTicket
        ) {
            dispatch(initializeBetMatchLine({ matchCount: 14, extraMatchCount: 1 }));
        }
    };
    // After select all lines in ticket, clear picked numbers
    useEffect(() => {
        if (playStore.lines.length > 0 && soccerPlayStore.betMatchLines.length === 0) {
            dispatch(setBetMatchLines({ lines: mapSessionLinesIntoBets(playStore.lines) }));
        } else if (soccerPlayStore.betMatchLines.length === 0) {
            dispatch(initializeBetMatchLine({ matchCount: 14, extraMatchCount: 1 }));
        }
    }, []);

    useEffect(() => {
        if (lastBetMatchLineRef.current) {
            lastBetMatchLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [soccerPlayStore.betMatchLines.length]);

    useEffect(() => {
        dispatch(setDefaultStakes(playStore.lines.length));
        if (soccerPlayStore.betMatchLines.length === 0) {
            return;
        }

        const validatedBetMatchLines = soccerPlayStore.betMatchLines.filter((line) => validateLineBets(line));
        const stakes = calculateStakesByLineBets(validatedBetMatchLines);
        dispatch(setStakes(stakes));
    }, [playStore.lines.length]);

    // Check if all lines in ticket are selected
    useEffect(() => {
        validateLines();
    }, [soccerPlayStore.betMatchLines]);

    return (
        <div className="scroll-wrapper" ref={scrollWrapper}>
            <MatchesList />
            {soccerPlayStore.betMatchLines.map((line, index) => {
                const isValidLine = validateLineBets(line);
                const isNewRow = index !== 0 && index % ticketsPerRow.tickets === 0;
                const isLastElement = index === soccerPlayStore.betMatchLines.length - 1;

                return (
                    <div key={`play-picker-${index + 1}`} className="d-inline-flex">
                        {isNewRow && (
                            <>
                                <div className="w-100" />
                                <MatchesList />
                            </>
                        )}
                        <div
                            key={`play-picker-${index + 1}`}
                            className={`play-picker ${isValidLine && 'selected'}`}
                            ref={isLastElement ? lastBetMatchLineRef : null}
                        >
                            <p className="font-bold text-center text-pick">
                                {isValidLine ? t('playPicker.ok') : t('playPicker.placeYourBet')}
                            </p>
                            <div className="row row-buttons no-gutters d-flex justify-content-between">
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary purchase-line-clear"
                                        onClick={() => clearPickedNumbers(index)}
                                    >
                                        {t('playPicker.clear')}
                                    </button>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-primary quick-pick"
                                        onClick={() => quickPickNumbers(index)}
                                    >
                                        {t('playPicker.quickPick')}
                                    </button>
                                </div>
                            </div>
                            <div className="purchase-line">
                                {line.map((e, lineIndex) => (
                                    <Numbers
                                        betLinesIndex={index}
                                        lineIndex={lineIndex}
                                        selected={e.map((num) => !!num)}
                                        key={`numbers-${index}-${lineIndex+1}`}
                                        disabled={false}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default Picker;
